/**
 * Who is signed in, as the header shows it.
 *
 * Sources, best first: the profile API (`GET /auth/api/profile-details`, which
 * answers `{ user: { email, full_name, role_name }, organization: {
 * organization_name } }`), then the `user` blob login wrote to localStorage.
 * Nothing here is invented: with no name anywhere the email stands in for it,
 * and with no organisation the line is simply left out — a super admin carries
 * no organization_id, so "no org" is a real state, not a gap to paper over.
 */

const clean = (v) => (typeof v === 'string' ? v.trim() : '');

/** First + last when both are known, else whichever single name field exists. */
const nameFrom = (u = {}) => {
  const first = clean(u.first_name || u.firstName || u.given_name);
  const last = clean(u.last_name || u.lastName || u.family_name);
  if (first || last) return [first, last].filter(Boolean).join(' ');
  return clean(u.full_name || u.fullName || u.display_name || u.displayName || u.name);
};

/**
 * Two letters from a name ("Priya Nair" → "PN", "Cher" → "CH"), or from an
 * email's local part split on . _ - + ("priya.nair@x" → "PN", "ops@x" → "OP").
 */
export const initialsOf = (name, email) => {
  const fromWords = (words) => {
    const w = words.filter(Boolean);
    if (w.length === 0) return '';
    if (w.length === 1) return w[0].slice(0, 2).toUpperCase();
    return `${w[0][0]}${w[w.length - 1][0]}`.toUpperCase();
  };
  const n = clean(name);
  if (n) {
    const got = fromWords(n.split(/\s+/).map((s) => s.replace(/[^\p{L}\p{N}]/gu, '')));
    if (got) return got;
  }
  const local = clean(email).split('@')[0] || '';
  const got = fromWords(local.split(/[._\-+]+/).map((s) => s.replace(/[^\p{L}\p{N}]/gu, '')));
  return got || '?';
};

/**
 * @param {object|null} profile  profile-details response (or null when it failed)
 * @param {object} stored        normalised localStorage user
 * @returns {{ name: string, email: string, hasName: boolean, initials: string,
 *             role: string, org: string }}
 */
export const resolveIdentity = (profile, stored = {}) => {
  const pUser = (profile && typeof profile.user === 'object' && profile.user) || {};
  const pOrg = (profile && typeof profile.organization === 'object' && profile.organization) || {};
  const email = clean(pUser.email) || clean(stored.email);
  const realName = nameFrom(pUser) || nameFrom(stored);
  // A "name" that is just the email again is not a name.
  const hasName = Boolean(realName) && realName.toLowerCase() !== email.toLowerCase();
  return {
    name: hasName ? realName : email,
    email,
    hasName,
    initials: initialsOf(hasName ? realName : '', email),
    role: clean(pUser.role_name) || clean(stored.role_name),
    org: clean(pOrg.organization_name) || clean(pUser.organization_name) || clean(stored.organization_name),
  };
};
